// Audio engine: broadcast layer (the district's live station) + pulse layer
// (designed ambient soundscape). The two layers are user-switchable
// (architecture v0.2 §4 "The audio model").
//
// Broadcast: streams the live now-playing item (track or DJ narration) from the
// shared API, keeping server-authoritative sync via seek_to_ms.
// Pulse: a procedural WebAudio soundscape so the slice carries place-tone with
// zero asset dependencies (real sound-design swaps in later).

import { SLICE_DISTRICT } from '../config';
import { api } from './api';
import { blessAudioPool } from './audioPool';

let audioEl = null;
let pulseCtx = null;
let pulseNodes = null;
let nowPlayingTimer = null;
let ducked = false;   // a full duck (DJ cameo / video call) owns the mix → dip() yields
// A ROOM HOLD is longer-lived than a duck: it lasts as long as you are inside a
// room that brings its own sound. `roomHeldSilent` remembers that the station
// was ALREADY off when you walked in, which is the difference between restoring
// the music and starting it uninvited.
let roomHeld = false;
let roomHeldSilent = false;
// The listener switched the station on THEMSELVES while it was held (the Music
// toggle, the radio wheel): theirs outranks the hold until the hold ends.
let heldButMine = false;
// Held quiet: nothing but the listener's own hand starts the station. Without
// this, the station's own re-sync at a track change, a venue's exit restore or
// a scene ending could start it in the middle of a live set, under a hold that
// then never fades it again (Khari, 2026-10-28: "we should mute it for them").
const heldQuiet = () => roomHeld && !heldButMine;
// WHO is holding the station quiet: a room with its own sound ('room'), a live
// set you are standing at ('stage'). One switch shared by two owners released
// it for both — walking from a set into the salon could bring the radio back
// inside — so the station stays quiet while ANY holder has it.
const quietHolders = new Set();
let fadeTimer = null;

const listeners = new Set();
export function onNowPlaying(cb) { listeners.add(cb); return () => listeners.delete(cb); }
function emit(np) { listeners.forEach((cb) => cb(np)); }

// A valid, entirely silent wav — just enough for play() to resolve so the
// gesture counts. Inline so unlocking never waits on the network.
import { SILENT_WAV } from './silentClip';

function ensureAudioEl() {
  if (!audioEl) {
    audioEl = new Audio();
    // Do NOT set crossOrigin='anonymous': the GCS bucket serving these streams
    // does not return Access-Control-Allow-Origin, so a CORS request would be
    // blocked and the audio would never play. A plain (no-cors) <audio> element
    // loads cross-origin media fine — we only need playback, not pixel/sample access.
    audioEl.preload = 'auto';
  }
  return audioEl;
}

async function syncBroadcast() {
  try {
    const data = await api.stationNow(SLICE_DISTRICT.stationSlug);
    emit(data);
    return data;
  } catch (e) {
    // Station may be offline in dev — stay silent, keep polling.
    return null;
  }
}

// Prime the broadcast during the loading screen: fetch /now and BUFFER the audio
// (set src + preload) WITHOUT playing — no gesture needed to buffer. Then the
// Enter click's play() is instant, so music sounds as the fly-in BEGINS instead
// of only after it finishes loading (~when the descent lands).
async function primeBroadcast() {
  try {
    const el = ensureAudioEl();
    const data = await syncBroadcast();
    if (!data?.current?.audio_url) return;
    if (el.src !== data.current.audio_url) el.src = data.current.audio_url;
    try { el.currentTime = Math.max(0, (data.seek_to_ms || 0) / 1000); } catch { /* not seekable yet */ }
    el.preload = 'auto';
    try { el.load(); } catch { /* noop */ }
  } catch { /* offline/dev — Enter falls back to ambient */ }
}

async function playBroadcast() {
  // Bail if the station is no longer the wanted layer (the user hit Mute since
  // this was queued) — otherwise an in-flight play (from the entry or the re-sync
  // timer) would un-mute the world. This is why Mute felt "broken" outside.
  if (baseLayer !== 'broadcast') return;
  if (heldQuiet()) return;   // a room or a live set holds it (it comes back when that ends)
  stopPulse();
  muteActiveVideo();      // the station is the source now — silence any playing clip
  const el = ensureAudioEl();
  // If already primed (buffered during the loader), start it NOW so playback
  // isn't gated behind the /now fetch below (and so play() runs inside the Enter
  // gesture). We re-sync the exact seek right after.
  if (el.src) { el.volume = 1.0; el.play().catch(() => {}); }
  const data = await syncBroadcast();
  if (baseLayer !== 'broadcast' || heldQuiet()) { el.pause(); return; } // muted / held during the fetch

  // If the station has nothing ready (offline/dev), fall back to the ambient
  // pulse so the world always carries sound, and keep polling for the station.
  if (!data || !data.playing || !data.current?.audio_url) {
    // Station has nothing ready — stay silent and keep polling (no WebAudio pulse).
    clearTimeout(nowPlayingTimer);
    nowPlayingTimer = setTimeout(playBroadcast, 10000);
    return;
  }

  const url = data.current.audio_url;
  if (el.src !== url) {
    el.src = url;
  }
  try {
    el.currentTime = Math.max(0, (data.seek_to_ms || 0) / 1000);
  } catch { /* not seekable yet */ }
  el.volume = 1.0;
  /* ⚠ NO PULSE ON A FAILED PLAY (2026-10-29: on an iPhone the radio stopped for
     a street source and never came back until Music was toggled). Coming back
     after a hold, this play() follows the primed fast-path play() and a src
     change, and iOS rejects the second (AbortError: interrupted by a new load).
     The old catch started the WebAudio pulse, which on iOS SEIZES the audio
     session (see setMusicOn), so the station could not sound again without a
     tap. Now: try again once the stream can play, a few times; if it still will
     not, stay quiet — the Music toggle (a tap) restarts it. */
  for (let attempt = 0; attempt < 4; attempt++) {
    try { await el.play(); playFailures = 0; break; }
    catch (err) {
      if (baseLayer !== 'broadcast' || heldQuiet()) return;
      if (attempt === 3) { playFailures += 1; console.warn('[audio] the station would not start:', err?.name || err); break; }
      await new Promise((ok) => {
        const done = () => { el.removeEventListener('canplay', done); ok(); };
        el.addEventListener('canplay', done);
        setTimeout(done, 700 * (attempt + 1));
      });
    }
  }

  // Re-sync when this item ends / at its remaining time.
  clearTimeout(nowPlayingTimer);
  const remaining = Math.max(5000, (data.remaining_ms || 30000) + 1500);
  nowPlayingTimer = setTimeout(playBroadcast, remaining);
}

// ── Pulse layer (procedural ambience: distant bass, room tone, street hiss) ──
function startPulse() {
  if (pulseCtx) return;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  pulseCtx = new Ctx();

  const master = pulseCtx.createGain();
  master.gain.value = 0.0;
  master.connect(pulseCtx.destination);

  // Distant club bass — slow LFO on a low sine (bleed-through-the-wall feel).
  const bass = pulseCtx.createOscillator();
  bass.type = 'sine';
  bass.frequency.value = 55;
  const bassGain = pulseCtx.createGain();
  bassGain.gain.value = 0.06;
  const lfo = pulseCtx.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = 1.6; // ~96bpm thump
  const lfoGain = pulseCtx.createGain();
  lfoGain.gain.value = 0.05;
  lfo.connect(lfoGain).connect(bassGain.gain);
  bass.connect(bassGain).connect(master);

  // Street hiss — filtered noise, very low.
  const noiseBuf = pulseCtx.createBuffer(1, pulseCtx.sampleRate * 2, pulseCtx.sampleRate);
  const d = noiseBuf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.4;
  const noise = pulseCtx.createBufferSource();
  noise.buffer = noiseBuf; noise.loop = true;
  const lp = pulseCtx.createBiquadFilter();
  lp.type = 'lowpass'; lp.frequency.value = 900;
  const noiseGain = pulseCtx.createGain();
  noiseGain.gain.value = 0.015;
  noise.connect(lp).connect(noiseGain).connect(master);

  bass.start(); lfo.start(); noise.start();
  // Fade in
  master.gain.linearRampToValueAtTime(0.9, pulseCtx.currentTime + 1.5);
  pulseNodes = { master, bass, lfo, noise };
}

function stopPulse() {
  if (!pulseCtx) return;
  try {
    const { master, bass, lfo, noise } = pulseNodes || {};
    if (master) master.gain.linearRampToValueAtTime(0, pulseCtx.currentTime + 0.6);
    setTimeout(() => {
      try { bass?.stop(); lfo?.stop(); noise?.stop(); } catch {}
      pulseCtx?.close();
      pulseCtx = null; pulseNodes = null;
    }, 700);
  } catch { pulseCtx = null; pulseNodes = null; }
}

function stopBroadcast() {
  clearTimeout(nowPlayingTimer);
  if (audioEl) { audioEl.pause(); }
}

// Audio exclusivity: only ONE source sounds at a time — the city-player track
// (audioEl) OR a single video billboard / HypeClip. Others keep looping muted,
// so their visuals stay alive without clashing.
// baseLayer = what plays when nothing has taken over (broadcast or ambient
// pulse) — a one-shot clip returns to it when it ends / is toggled off.
let activeVideo = null;
let baseLayer = 'pulse';
let playFailures = 0;   // the station's play() refused after its retries (audio debug)

function pulseGain(v, ramp = 0.25) {
  if (pulseCtx && pulseNodes?.master) pulseNodes.master.gain.linearRampToValueAtTime(v, pulseCtx.currentTime + ramp);
}
function resumeBase() {
  if (baseLayer === 'broadcast') playBroadcast();
  else stopPulse(); // muted → stay silent (no WebAudio session grab)
}

// ── Central mix: base ‹ foreground ‹ override ─────────────────────────────────
// Only ONE thing is heard at a time, arbitrated here so the cinema movie, the
// district station, and the hover-news never fight.
//   base       = the station (broadcast) or ambient pulse (the default bed)
//   foreground = a media element that OWNS the bed while present (the cinema
//                movie); its audibility follows the venue's Music toggle
//   override   = a momentary top source (the news narration on hover) that
//                silences whatever is under it, then hands the mix back
let foreground = null;        // HTMLMediaElement | null
let foregroundMuted = false;  // set by the venue from its Music toggle
let override = null;          // HTMLMediaElement | null

// ── AMBIENT: things that play WITH the bed, never instead of it ──────────────
//
// Khari, 2026-08-04: "it stops the background music.. let the user decide that.
// dont mute it. there will be times where this kills the scene" — walking into
// a screen's bounds was killing the station.
//
// A television in a room is not an override. An override is the right shape for
// something that OWNS the moment (the cinema movie, a scene's narration): it
// silences what is under it and hands the mix back afterwards. A screen playing
// in the corner is the opposite — it is part of the room, and a room where the
// music stops the moment you step toward the TV is a room with a bug in it.
//
// So ambients never set `suspendBase`. They sound on top of the station at their
// own element volume, which the placement layer is already computing from
// distance. Whether that is too much is the listener's call, made with their
// feet and with the mute button — not ours, made by silencing the city.
const ambients = new Set();

function applyMix() {
  // Deliberately NOT `|| ambients.size` — that is the whole point of this layer.
  const suspendBase = !!(foreground || override);
  if (suspendBase) { stopBroadcast(); pulseGain(0.0, 0.15); }
  // Ambients keep sounding whatever else is happening; an override is loud
  // enough to sit over them on its own, and muting them here would reintroduce
  // exactly the "the room went silent" problem one layer down.
  ambients.forEach((el) => {
    try { el.muted = false; if (el.paused) el.play?.().catch(() => {}); } catch { /* noop */ }
  });
  if (foreground) {
    const audible = !override && !foregroundMuted;
    try { foreground.muted = !audible; if (audible) foreground.play?.().catch(() => {}); } catch { /* noop */ }
  }
  if (override) {
    try { override.muted = false; override.play?.().catch(() => {}); } catch { /* noop */ }
  }
}
function muteActiveVideo() {
  const v = activeVideo;
  activeVideo = null;
  if (!v) return;
  try {
    v.onended = null;
    v.muted = true;
    v.loop = true;                     // return it to a silent, looping visual
    if (v.paused) v.play().catch(() => {});
  } catch { /* noop */ }
}

export const audioEngine = {
  // musicOn=true → broadcast layer; false → pulse layer. Toggling the music
  // takes the audio back from any playing video/HypeClip (it returns to a
  // silent visual loop).
  /**
   * BLESS THE AUDIO ELEMENT WITHOUT PLAYING ANYTHING.
   *
   * Khari, 2026-08-07: "halt the music at the entrance of the world... The radio
   * should instead start with the fly-in."
   *
   * The trouble is that the Enter click is not just a click, it is the USER
   * GESTURE — the only moment a browser will let audio begin. Simply moving
   * `setMusicOn(true)` later means the station tries to start from a timer or an
   * effect, and iOS refuses it: silence, no error, nothing in the log.
   *
   * So the gesture is spent here on a muted play/pause of the very element the
   * station will later use. That marks it as user-initiated for the rest of the
   * session, and a real `play()` afterwards is allowed from anywhere. Nothing is
   * audible — same element, volume zero, paused within the same tick.
   */
  async unlock() {
    // The city's own players (the plaza stage, the street's voices) borrow
    // elements blessed on this same tap (services/audioPool).
    blessAudioPool();
    const el = ensureAudioEl();
    const vol = el.volume;
    try {
      el.volume = 0;
      // A src is required before play() resolves at all. The station sets its
      // own the moment it starts, so whatever is here now is irrelevant — this
      // only has to be a valid, silent no-op.
      if (!el.src) el.src = SILENT_WAV;
      await el.play();
      el.pause();
      try { el.currentTime = 0; } catch { /* not seekable yet — harmless */ }
    } catch (err) {
      // Not fatal: the station will simply try again on its own, and on desktop
      // it will succeed. Worth saying, because "the radio never came on" is
      // otherwise a silent mystery.
      console.warn('[audio] could not pre-authorise playback on this gesture:', err?.message || err);
    } finally {
      el.volume = vol;
    }
  },

  /**
   * @param on
   * @param mine  the listener's own hand (the HUD Music toggle, the radio wheel):
   *   it outranks a room or a live set holding the station. Anything else that
   *   turns the station on during a hold (entering, leaving a venue, a scene
   *   ending) only says it should be on once the hold is over.
   */
  async setMusicOn(on, { mine = false } = {}) {
    baseLayer = on ? 'broadcast' : 'pulse';
    muteActiveVideo();
    if (roomHeld) {
      if (mine) heldButMine = on;
      else if (on) { roomHeldSilent = false; return; }
    }
    if (on) { await playBroadcast(); }
    // Music OFF = SILENCE (pause the station). We do NOT start the WebAudio pulse
    // here: on iOS it seizes the single audio session, so resuming the HTML
    // station audio on the next un-mute silently failed ("can't un-mute").
    else { stopBroadcast(); stopPulse(); }
  },
  async resync() { await syncBroadcast(); },
  /**
   * Retune the street's radio (the radio wheel). Per visitor and per session:
   * it moves SLICE_DISTRICT.stationSlug, which everything that asks "what is
   * the district playing" already reads at call time — the ticker, the beat's
   * modulation track, the re-sync timer. Nothing is written anywhere; a reload
   * is back on the backend's WORLD_STATION.
   *
   * The old station's src is cleared first, because playBroadcast starts any
   * src already on the element immediately (the primed-at-load fast path) —
   * which would be the station you just turned away from.
   */
  async switchStation(slug) {
    if (!slug) return;
    SLICE_DISTRICT.stationSlug = slug;
    clearTimeout(nowPlayingTimer);
    if (roomHeld) heldButMine = true;   // tuning in is the listener's own hand
    const el = ensureAudioEl();
    try { el.pause(); el.removeAttribute('src'); el.load(); } catch { /* noop */ }
    baseLayer = 'broadcast';
    await playBroadcast();
  },
  // Buffer the station during the loading screen so Enter plays instantly.
  async prime() { await primeBroadcast(); },
  // City player: play a specific featured track (clicked off a city sign) and
  // announce it as now-playing so the HUD ticker shows it. Stops the station.
  /**
   * Play a track as the city player.
   *
   * `seekMs` exists for the LISTENING ROOM, where everybody is hearing the same
   * record and the host started it before you sat down. The room stores when the
   * song began (an epoch stamp) rather than streaming a position, so a client
   * works out its own offset and lands there — which is what makes a late
   * arrival hear the middle of the song instead of the start of it.
   */
  async playTrack(track, { seekMs = 0 } = {}) {
    if (!track?.audio_url) { console.warn('[cityPlayer] no audio_url on track', track); return; }
    stopPulse();
    stopBroadcast();
    muteActiveVideo(); // a track takes over — silence any sounding video billboard
    const el = ensureAudioEl();
    el.pause();
    el.src = track.audio_url;
    el.loop = false;
    el.volume = 1;
    if (seekMs > 0) {
      // Set BEFORE play() where the browser allows it, and again on metadata —
      // a fresh src is not seekable yet, so the first assignment is silently
      // dropped and the room would all start from zero at different moments.
      const seek = () => { try { el.currentTime = seekMs / 1000; } catch { /* not ready */ } };
      seek();
      el.addEventListener('loadedmetadata', seek, { once: true });
    }
    try { await el.play(); } catch (err) { console.error('[cityPlayer] play() failed:', err?.message || err); }
    emit({
      playing: true,
      current: { item_type: 'track', track_id: track.track_id, title: track.title, artist: track.avatar_name || track.artist, audio_url: track.audio_url },
      seek_to_ms: seekMs || 0,
    });
  },
  // Play a video billboard / HypeClip's own audio ONCE, as the sole source.
  // Clicking the one that's already sounding TOGGLES it off. When its single
  // play finishes (or it's toggled / interrupted by other music), it returns to
  // a silent looping visual and the base layer (station/pulse) comes back.
  playVideo(videoEl) {
    if (!videoEl) return;
    // Toggle: a second click on the sounding clip stops it.
    if (activeVideo === videoEl) { muteActiveVideo(); resumeBase(); return; }
    stopPulse();
    stopBroadcast();
    if (audioEl) audioEl.pause();
    muteActiveVideo();              // silence + restore any other clip
    videoEl.muted = false;
    videoEl.loop = false;           // play through ONCE, not on a loop
    try { videoEl.currentTime = 0; } catch { /* not seekable yet */ }
    videoEl.onended = () => { muteActiveVideo(); resumeBase(); };
    videoEl.play().catch(() => {});
    activeVideo = videoEl;
  },

  // ── Foreground (venue movie) ──────────────────────────────────────────────
  // Hand a media element the sound bed: the station/pulse go silent under it.
  // Audibility of the element itself follows setForegroundMuted (the venue's
  // Music toggle). Call again with the next clip's element to hand off cleanly.
  setForeground(el) { foreground = el || null; applyMix(); },
  setForegroundMuted(muted) { foregroundMuted = !!muted; applyMix(); },
  clearForeground(el) {
    if (el && foreground !== el) return;   // a newer clip already took over
    foreground = null;
    applyMix();
    if (!override) resumeBase();
  },
  // ── Override (hover-news narration) ───────────────────────────────────────
  // Momentarily take the very top of the mix: silence the foreground (movie) and
  // the base (station), play `el`; popping restores whatever was underneath.
  // ── Ambient (a screen playing in the room) ────────────────────────────────
  // Sounds ALONGSIDE the station rather than instead of it. Use this for
  // anything that is part of a room's background; use pushOverride only for
  // something that genuinely owns the moment.
  addAmbient(el) { if (!el) return; ambients.add(el); applyMix(); },
  removeAmbient(el) {
    if (!el || !ambients.delete(el)) return;
    try { el.pause?.(); } catch { /* noop */ }
    // No resumeBase(): the bed was never suspended, so there is nothing to
    // restore — which is the property that keeps a scene intact.
  },

  pushOverride(el) { if (!el) return; override = el; applyMix(); },
  popOverride(el) {
    if (override !== el) return;
    try { el.pause?.(); } catch { /* noop */ }
    override = null;
    applyMix();
    if (!foreground) resumeBase();   // foreground, if any, is re-voiced by applyMix
  },

  // Duck the world's sound bed — the speaker owns the mix, and the station comes
  // back up afterwards.
  //
  // Two depths, because there are two situations. A DJ cameo talks OVER the
  // record on purpose, so the music dips and stays audible under them. A CALL is
  // different: you are listening to a person, the music is not part of it, and a
  // bed at 10% is just something you can't quite ignore. Callers that are calls
  // pass `full` and get silence.
  duck(on, full = false) {
    ducked = on;
    if (audioEl) audioEl.volume = on ? (full ? 0 : 0.1) : 1;
    if (pulseCtx && pulseNodes?.master) {
      pulseNodes.master.gain.linearRampToValueAtTime(on ? (full ? 0 : 0.08) : 0.9, pulseCtx.currentTime + 0.4);
    }
  },
  // Position (seconds) within the current station track, or null when the
  // broadcast isn't the source (pulse-layer / paused). Beat-reactive visuals
  // anchor their phase to THIS — it resets every track, so a slightly-off BPM
  // can't accumulate drift (the "sways on and off beat" problem) across a set,
  // and it's server-synced so every viewer pulses together.
  broadcastTime() { return audioEl && !audioEl.paused ? audioEl.currentTime : null; },
  // Gentle, momentary dip so a resident's passing bark stays intelligible over
  // the station — lighter than duck(), and a no-op while a full duck (DJ cameo /
  // video call) already owns the mix, so the two never fight.
  dip(on) {
    if (ducked) return;
    if (audioEl) audioEl.volume = on ? 0.5 : 1;
    if (pulseCtx && pulseNodes?.master) {
      pulseNodes.master.gain.linearRampToValueAtTime(on ? 0.4 : 0.9, pulseCtx.currentTime + 0.15);
    }
  },
  // ── A ROOM WITH ITS OWN SOUND ────────────────────────────────────────────
  //
  // Khari, 2026-08-06: "when the user enters the salon, fade the main music out
  // (if it is not already muted).. the rooms have their own ambiance.. The salon
  // has gossip and the news on the TV.. Sometimes live.. concert halls, for
  // example. When the user leaves the room, fade the main music back on (if it
  // was on when they entered)."
  //
  // This is NOT duck(). A duck dips the station and yields the mix for a moment
  // — a DJ talking over a record, a bark, a call. Here the room REPLACES the bed
  // entirely and for as long as you are in it, so the station fades out and
  // stops rather than sitting at 10% under the gossip, where it would read as a
  // radio nobody turned off.
  //
  // Two rules, both from the ask:
  //   · Silence is only ever RESTORED, never imposed. If the station was
  //     already off when they walked in, leaving is not an excuse to start it.
  //   · The user's own toggle outranks the room. Turn the music on inside the
  //     salon and it stays on — `baseLayer` is the preference, and this only
  //     ever moves the volume of a preference that is already 'on'.
  async roomQuiet(on, ms = 900, who = 'room') {
    if (on) quietHolders.add(who); else quietHolders.delete(who);
    if (!on && quietHolders.size) return;   // someone else still holds it
    if (fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; }
    const ramp = (to) => new Promise((done) => {
      const el = audioEl;
      const from = el ? el.volume : (to === 0 ? 1 : 0);
      const t0 = Date.now();
      if (pulseCtx && pulseNodes?.master) {
        pulseNodes.master.gain.linearRampToValueAtTime(to === 0 ? 0 : 0.9, pulseCtx.currentTime + ms / 1000);
      }
      if (!el) { done(); return; }
      fadeTimer = setInterval(() => {
        const k = Math.min(1, (Date.now() - t0) / ms);
        el.volume = Math.max(0, Math.min(1, from + (to - from) * k));
        if (k >= 1) { clearInterval(fadeTimer); fadeTimer = null; done(); }
      }, 40);
    });

    if (on) {
      if (roomHeld) return;
      roomHeld = true;
      heldButMine = false;
      // "if it is not already muted" — nothing to fade, and nothing to restore.
      if (baseLayer !== 'broadcast') { roomHeldSilent = true; return; }
      roomHeldSilent = false;
      await ramp(0);
      stopBroadcast();
      // Leave the element at full volume once it is paused: anything else that
      // starts the station while we are inside (the HUD toggle, a listening
      // room) must not inherit a silent player.
      if (audioEl) audioEl.volume = 1;
      return;
    }

    if (!roomHeld) return;
    roomHeld = false;
    heldButMine = false;
    // It was off when they walked in, so it stays off when they walk out.
    if (roomHeldSilent || baseLayer !== 'broadcast') return;
    // Already sounding = they turned it back on themselves while inside. Their
    // choice already happened; a fade here would only be an audible blip.
    if (audioEl && !audioEl.paused) { audioEl.volume = 1; return; }
    if (audioEl) audioEl.volume = 0;
    await playBroadcast();
    await ramp(1);
  },
  /** Is the station being held quiet (a room's own sound, a live set)? */
  stationHeld() { return quietHolders.size > 0; },
  /** Held, but the listener tuned the station back in by hand (RadioWheel): their choice — a room's own soundtrack steps aside (RoomSoundtrack). */
  tunedInWhileHeld() { return roomHeld && heldButMine; },
  /** What the station is doing, for `?audiodebug` (components/AudioDebug). */
  debugState() {
    return {
      layer: baseLayer, paused: audioEl ? audioEl.paused : null, ready: audioEl ? audioEl.readyState : null,
      holders: [...quietHolders], held: heldQuiet(), pulse: !!pulseCtx, failures: playFailures,
    };
  },
  stop() { stopBroadcast(); stopPulse(); }
};